import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X, ChevronRight, Share2 } from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/oetdot0e_SalesAssets%20Logo.png";

const Navbar = ({ variant = 'default' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isTransparent = variant === 'transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${isTransparent ? 'bg-transparent' : 'bg-white border-b border-slate-100'}`}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="SalesAssets" 
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className={`font-bold text-lg ${isTransparent ? 'text-white' : 'text-[#0a2540]'}`}>
              SalesAssets
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                <Link 
                  to="/library" 
                  className={`text-sm font-medium transition-colors ${isTransparent ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-[#0a2540]'}`}
                >
                  My Assets
                </Link>
                <Link 
                  to="/connections" 
                  className={`text-sm font-medium transition-colors ${isTransparent ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-[#0a2540]'}`}
                  data-testid="connections-link"
                >
                  Connections
                </Link>
                <Link 
                  to="/share" 
                  className={`text-sm font-medium transition-colors flex items-center gap-1 ${isTransparent ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-[#0a2540]'}`}
                  data-testid="share-link"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share
                </Link>
                <Button 
                  onClick={handleLogout}
                  variant="ghost"
                  className={`text-sm ${isTransparent ? 'text-white hover:bg-white/10' : ''}`}
                  data-testid="logout-btn"
                >
                  Log out
                </Button>
                <Button 
                  onClick={() => navigate('/create')}
                  className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-10 px-5"
                  data-testid="create-asset-btn"
                >
                  Create Asset
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className={`text-sm ${isTransparent ? 'text-white hover:bg-white/10' : ''}`}
                  data-testid="login-btn"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => navigate('/signup')}
                  className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-10 px-5"
                  data-testid="signup-btn"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className={`w-6 h-6 ${isTransparent ? 'text-white' : 'text-[#0a2540]'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isTransparent ? 'text-white' : 'text-[#0a2540]'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 animate-fade-in">
            <div className="flex flex-col gap-2">
              {user ? (
                <>
                  <Link 
                    to="/library" 
                    className="px-4 py-3 text-slate-600 hover:bg-slate-50 flex items-center justify-between"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Assets
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/connections" 
                    className="px-4 py-3 text-slate-600 hover:bg-slate-50 flex items-center justify-between"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-connections-link"
                  >
                    Connections
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link 
                    to="/share" 
                    className="px-4 py-3 text-slate-600 hover:bg-slate-50 flex items-center justify-between"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-share-link"
                  >
                    <span className="flex items-center gap-2">
                      <Share2 className="w-4 h-4" />
                      Share
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <div className="px-4 pt-2 flex flex-col gap-2">
                    <Button 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate('/create');
                      }}
                      className="w-full bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028]"
                      data-testid="mobile-create-btn"
                    >
                      Create Asset
                    </Button>
                    <Button 
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      variant="outline"
                      className="w-full"
                      data-testid="mobile-logout-btn"
                    >
                      Log out
                    </Button>
                  </div>
                </>
              ) : (
                <div className="px-4 pt-2 flex flex-col gap-2">
                  <Button 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/signup');
                    }}
                    className="w-full bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028]"
                    data-testid="mobile-signup-btn"
                  >
                    Get Started Free
                  </Button>
                  <Button 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/login');
                    }}
                    variant="outline"
                    className="w-full"
                    data-testid="mobile-login-btn"
                  >
                    Log in
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
